/**
 * Anmeldezustand der SPA. Access- und ID-Token nur im Speicher; das Refresh-Token
 * liegt wie bei der Cognito-SDK-Speicherung im `localStorage` (APP_KONZEPT.md,
 * Auth-Fluss Punkt 4), damit ein Neuladen keine erneute Anmeldung verlangt.
 */
import { AuthError, decodeJwtPayload, type CognitoClient, type Tokens } from './cognito.js';

export interface Session {
  accessToken: string;
  expiresAt: number;
  email?: string;
}

export type AuthState =
  | { status: 'restoring' }
  | { status: 'signed-out' }
  | { status: 'signed-in'; session: Session };

export interface KeyValueStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
  /** Änderungen aus anderen Tabs (Browser-`storage`-Ereignis). */
  onExternalChange?(key: string, listener: () => void): () => void;
}

export const REFRESH_KEY = 'pat.auth.refresh';
/** Access-Token so lange vor Ablauf erneuern. */
const REFRESH_MARGIN_MS = 2 * 60 * 1000;
/** Entwicklungsidentität des lokalen API-Servers (Fixture `fixtureMember.userId`). */
const DEV_USER_ID = 'cognito-sub-0001';

export function browserStorage(): KeyValueStorage {
  return {
    get: key => {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        /* Speicher gesperrt: Sitzung gilt nur bis zum Neuladen */
      }
    },
    remove: key => {
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* ignorieren */
      }
    },
    onExternalChange: (key, listener) => {
      const handler = (event: StorageEvent) => {
        if (event.key === key || event.key === null) listener();
      };
      window.addEventListener('storage', handler);
      return () => window.removeEventListener('storage', handler);
    },
  };
}

export interface AuthStore {
  getState(): AuthState;
  subscribe(listener: () => void): () => void;
  restore(): Promise<void>;
  signIn(email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  /**
   * Gültiges Access-Token; `undefined`, wenn die Sitzung beendet ist. Wirft `AuthError`
   * bei vorübergehenden Fehlern (Netz, Drosselung), ohne die Sitzung zu beenden.
   */
  getAccessToken(): Promise<string | undefined>;
  forgotPassword(email: string): Promise<void>;
  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void>;
}

function sessionFrom(tokens: Tokens): Session {
  const claims = decodeJwtPayload(tokens.idToken);
  return {
    accessToken: tokens.accessToken,
    expiresAt: tokens.expiresAt,
    ...(typeof claims['email'] === 'string' ? { email: claims['email'] } : {}),
  };
}

function createStateHolder() {
  let state: AuthState = { status: 'restoring' };
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set(next: AuthState) {
      state = next;
      for (const listener of listeners) listener();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export function createCognitoAuthStore(client: CognitoClient, storage: KeyValueStorage, now: () => number = () => Date.now()): AuthStore {
  const holder = createStateHolder();
  let refreshing: Promise<string> | undefined;
  // Jede An- oder Abmeldung erhöht die Generation; ältere Refresh-Ergebnisse verfallen.
  let generation = 0;

  async function refreshSession(): Promise<string> {
    const started = generation;
    const refreshToken = storage.get(REFRESH_KEY);
    if (!refreshToken) {
      holder.set({ status: 'signed-out' });
      throw new AuthError('invalid-credentials');
    }
    try {
      const tokens = await client.refresh(refreshToken);
      if (started !== generation || storage.get(REFRESH_KEY) !== refreshToken) throw new AuthError('invalid-credentials');
      const session = sessionFrom(tokens);
      holder.set({ status: 'signed-in', session });
      return session.accessToken;
    } catch (error) {
      if (started !== generation) throw error instanceof AuthError ? error : new AuthError('unknown');
      // Nur ein abgelehntes Refresh-Token beendet die Sitzung; Netz- und Drosselfehler nicht.
      if (error instanceof AuthError && error.code === 'invalid-credentials') {
        if (storage.get(REFRESH_KEY) === refreshToken) storage.remove(REFRESH_KEY);
        holder.set({ status: 'signed-out' });
        throw error;
      }
      if (holder.get().status === 'restoring') holder.set({ status: 'signed-out' });
      throw error instanceof AuthError ? error : new AuthError('network');
    }
  }

  function singleFlightRefresh(): Promise<string> {
    refreshing ??= refreshSession().finally(() => {
      refreshing = undefined;
    });
    return refreshing;
  }

  async function signOut(): Promise<void> {
    generation += 1;
    const refreshToken = storage.get(REFRESH_KEY);
    storage.remove(REFRESH_KEY);
    holder.set({ status: 'signed-out' });
    if (refreshToken) await client.revoke(refreshToken).catch(() => undefined);
  }

  // Abmelden oder Anmelden in einem anderen Tab beendet die Sitzung hier ebenfalls;
  // ein neues Refresh-Token wird beim nächsten Bedarf frisch eingelesen.
  storage.onExternalChange?.(REFRESH_KEY, () => {
    generation += 1;
    holder.set({ status: storage.get(REFRESH_KEY) ? 'restoring' : 'signed-out' });
  });

  return {
    getState: holder.get,
    subscribe: holder.subscribe,
    async restore() {
      await singleFlightRefresh().catch(() => undefined);
    },
    async signIn(email, password) {
      // Neuer Benutzer im selben Browser: alte Sitzung widerrufen und Cache leeren lassen.
      if (holder.get().status === 'signed-in' || storage.get(REFRESH_KEY)) await signOut();
      const tokens = await client.signIn(email, password);
      generation += 1;
      if (tokens.refreshToken) storage.set(REFRESH_KEY, tokens.refreshToken);
      holder.set({ status: 'signed-in', session: sessionFrom(tokens) });
    },
    signOut,
    async getAccessToken() {
      const state = holder.get();
      if (state.status === 'signed-in' && state.session.expiresAt - REFRESH_MARGIN_MS > now()) return state.session.accessToken;
      if (state.status === 'signed-out') return undefined;
      try {
        return await singleFlightRefresh();
      } catch (error) {
        if (holder.get().status === 'signed-out') return undefined;
        throw error; // vorübergehend: Sitzung bleibt, Anfrage schlägt fehl
      }
    },
    forgotPassword: email => client.forgotPassword(email),
    confirmForgotPassword: (email, code, newPassword) => client.confirmForgotPassword(email, code, newPassword),
  };
}

/** Nur Vite-Dev-Server: Anmeldung ohne Cognito gegen den lokalen API-Server. */
/** `storage` (nur Vite-Dev-Server): angemeldet bleiben über Seitenwechsel, etwa die Rückkehr von eBay. */
export function createDevAuthStore(storage?: KeyValueStorage): AuthStore {
  const holder = createStateHolder();
  const key = 'pat.devSession';
  const session = (email: string): Session => ({ accessToken: `dev:${DEV_USER_ID}`, expiresAt: Number.MAX_SAFE_INTEGER, email });
  return {
    getState: holder.get,
    subscribe: holder.subscribe,
    async restore() {
      const email = storage?.get(key);
      holder.set(email ? { status: 'signed-in', session: session(email) } : { status: 'signed-out' });
    },
    async signIn(email) {
      const name = email.trim() || 'entwicklung@lokal.test';
      storage?.set(key, name);
      holder.set({ status: 'signed-in', session: session(name) });
    },
    async signOut() {
      storage?.remove(key);
      holder.set({ status: 'signed-out' });
    },
    async getAccessToken() {
      const state = holder.get();
      return state.status === 'signed-in' ? state.session.accessToken : undefined;
    },
    async forgotPassword() {},
    async confirmForgotPassword() {},
  };
}
